/** Presentation helpers for the dashboard. Pure — unit-tested. */

/** Coin scale — smallest-unit integers on the wire, this many minor digits. */
export const GG_DECIMALS = 2;

/**
 * Format a smallest-unit integer amount as a decimal string — "2500" → "25.00".
 * Matches the wallet's formatting so a buy reads the same magnitude in both
 * apps. Malformed input passes through unchanged.
 */
export function formatMoney(amount: string | bigint, decimals = GG_DECIMALS): string {
  const raw = typeof amount === "bigint" ? amount.toString() : amount;
  if (!/^-?\d+$/.test(raw)) return raw;
  const negative = raw.startsWith("-");
  const digits = (negative ? raw.slice(1) : raw).padStart(decimals + 1, "0");
  const whole = digits.slice(0, digits.length - decimals);
  const frac = digits.slice(digits.length - decimals);
  return `${negative ? "-" : ""}${whole}.${frac}`;
}

/**
 * Format an ISO 8601 timestamp as a short UTC date-time in English — fixed
 * locale + UTC so it reads the same regardless of the viewer's system language.
 */
export function formatDateTime(iso: string): string {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
}

/** Format an optional expiry — `null` means a non-expiring key. */
export function formatExpiry(iso: string | null): string {
  return iso ? formatDateTime(iso) : "Never";
}

/** Format an optional ISO timestamp that may be null (e.g. a pending posting). */
export function formatMaybeDateTime(iso: string | null): string {
  return iso ? formatDateTime(iso) : "—";
}
